/**
 * Simple chart components for Admin Dashboard
 * Using pure CSS/SVG for lightweight visualization
 */

import React from 'react';

// Bar Chart component
export function SimpleBarChart({ data, height = 200, barColor = '#10b981' }) {
  if (!data || data.length === 0) return null;
  
  const maxValue = Math.max(...data.map(d => d.value || 0), 1);
  const barWidth = 100 / data.length;
  
  return (
    <div className="relative" style={{ height }}>
      {/* Y-axis labels */}
      <div className="absolute left-0 top-0 bottom-6 w-12 flex flex-col justify-between text-xs text-slate-500">
        <span>${(maxValue / 1).toFixed(0)}</span>
        <span>${(maxValue / 2).toFixed(0)}</span>
        <span>$0</span>
      </div>
      
      {/* Chart area */}
      <div className="ml-14 h-full pb-6 flex items-end gap-1">
        {data.map((item, i) => (
          <div 
            key={i} 
            className="flex-1 flex flex-col items-center"
          >
            {/* Bar */}
            <div 
              className="w-full rounded-t transition-all duration-300 hover:opacity-80"
              style={{ 
                height: `${(item.value / maxValue) * 100}%`,
                backgroundColor: barColor,
                minHeight: item.value > 0 ? '4px' : '0'
              }}
              title={`${item.label}: $${item.value?.toFixed(2) || 0}`}
            />
            {/* Label */}
            <span className="text-xs text-slate-500 mt-1 truncate w-full text-center">
              {item.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Donut Chart component
export function SimpleDonutChart({ data, size = 160, thickness = 30 }) {
  if (!data || data.length === 0) return null;
  
  const total = data.reduce((sum, d) => sum + (d.value || 0), 0);
  if (total === 0) return null;
  
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  
  let offset = 0;
  const segments = data.map((item, i) => {
    const percent = (item.value / total) * 100;
    const dashLength = (percent / 100) * circumference;
    const dashOffset = -offset;
    offset += dashLength;
    
    return {
      ...item,
      percent,
      dashLength,
      dashOffset
    };
  });
  
  return (
    <div className="flex items-center gap-6">
      {/* Donut */}
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="transform -rotate-90">
          {segments.map((seg, i) => (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={seg.color}
              strokeWidth={thickness}
              strokeDasharray={`${seg.dashLength} ${circumference}`}
              strokeDashoffset={seg.dashOffset}
              className="transition-all duration-300"
            />
          ))}
        </svg>
        {/* Center text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold text-white">{total}</span>
          <span className="text-xs text-slate-400">Orders</span>
        </div>
      </div>
      
      {/* Legend */}
      <div className="space-y-2">
        {segments.map((seg, i) => (
          <div key={i} className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: seg.color }}
            />
            <span className="text-sm text-slate-400">{seg.label}</span>
            <span className="text-sm text-white font-medium ml-auto">
              {seg.value} ({seg.percent.toFixed(0)}%)
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Sparkline component
export function Sparkline({ data, color = '#10b981', height = 40 }) {
  if (!data || data.length < 2) return null;
  
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  
  const points = data.map((value, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = 100 - ((value - min) / range) * 100;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <svg 
      viewBox="0 0 100 100" 
      preserveAspectRatio="none"
      style={{ height, width: '100%' }}
    >
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="2"
        points={points}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

// Heatmap component (for hourly sales)
export function SimpleHeatmap({ data, rows = 7, cols = 24 }) {
  if (!data || data.length === 0) return null;
  
  const maxValue = Math.max(...data.map(d => d.value || 0), 1);
  
  const getColor = (value) => {
    const intensity = value / maxValue;
    if (intensity < 0.2) return 'bg-slate-800';
    if (intensity < 0.4) return 'bg-emerald-900/50';
    if (intensity < 0.6) return 'bg-emerald-700/50';
    if (intensity < 0.8) return 'bg-emerald-500/50';
    return 'bg-emerald-400';
  };
  
  return (
    <div className="space-y-1">
      {/* Hour labels */}
      <div className="flex gap-1 ml-12">
        {Array.from({ length: cols }).map((_, i) => (
          <div 
            key={i} 
            className="flex-1 text-center text-xs text-slate-500"
          >
            {i % 4 === 0 ? `${i}h` : ''}
          </div>
        ))}
      </div>
      
      {/* Grid */}
      <div className="flex gap-1">
        {/* Day labels */}
        <div className="flex flex-col gap-1 text-xs text-slate-500 w-10">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
            <div key={d} className="h-4 flex items-center">{d}</div>
          ))}
        </div>
        
        {/* Heatmap cells */}
        <div className="flex-1 grid gap-1" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
          {data.map((cell, i) => (
            <div
              key={i}
              className={`h-4 rounded-sm ${getColor(cell.value)}`}
              title={`${cell.label}: $${cell.value?.toFixed(0) || 0}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
